import { useState, type FormEvent, type ReactNode } from 'react';
import { ChevronLeft, CircleAlert, ShieldCheck } from 'lucide-react';
import { BRAND } from '@/lib/brand';
import { Button, Field, Input, Logo, PasswordInput, ThemeSwitch } from '@/components/ui';
import { AuthenticatorSetup } from '@/components/account/AuthenticatorSetup';
import { useCompleteEnrollment, useSignIn } from '@/hooks/useAuth';
import { errorCode, errorDetails, errorField, errorText } from '@/utils/errors';
import type { SignInResult } from '@/types/api';

interface Enrollment {
  readonly token: string;
  readonly secret: string;
  readonly otpauthUri: string;
}

/** Reads the binding offer the server attaches to a sign-in it will not finish
 *  until an authenticator is set up. */
function enrollmentFrom(error: unknown): Enrollment | null {
  const details = errorDetails(error);
  if (details['mfaEnrollmentRequired'] !== true) return null;

  const offer = details['enrollment'] as Partial<Enrollment> | undefined;
  if (!offer?.token || !offer.secret || !offer.otpauthUri) return null;

  return { token: offer.token, secret: offer.secret, otpauthUri: offer.otpauthUri };
}

export function SignInPage({ onSignedIn }: { readonly onSignedIn: (user: SignInResult['user']) => void }): ReactNode {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);

  const mutation = useSignIn({
    onSuccess: (result) => onSignedIn(result.user),
    onError: (error) => {
      // The backend says whether a code is needed; the form does not guess.
      if (errorDetails(error)['mfaRequired'] === true) setMfaRequired(true);

      const offer = enrollmentFrom(error);
      if (offer) setEnrollment(offer);
    },
  });

  const enroll = useCompleteEnrollment({ onSuccess: (result) => onSignedIn(result.user) });

  // Being asked for a code is not a failed attempt, and showing it in red would
  // tell the operator they got something wrong when they did not.
  const answer = errorDetails(mutation.error);
  const prompting = answer['mfaRequired'] === true || answer['mfaEnrollmentRequired'] === true;
  const failed = mutation.isError && !prompting;

  // On this screen UNAUTHENTICATED means "what you just typed was refused",
  // which is a different sentence from the one it means anywhere else. The
  // server will not say which field was wrong, and neither does this.
  const message =
    errorCode(mutation.error) === 'UNAUTHENTICATED'
      ? mfaRequired
        ? 'Неверный пароль или код подтверждения'
        : 'Неверный логин или пароль'
      : errorText(mutation.error, 'Не удалось войти');

  const enrollError = !enroll.isError
    ? null
    : errorField(enroll.error) === 'token'
      ? 'Срок привязки истёк или попытки кончились. Вернитесь и войдите заново.'
      : errorField(enroll.error) === 'code'
        ? 'Код не подошёл'
        : errorText(enroll.error, 'Не удалось привязать приложение');

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    mutation.mutate({ login, password, ...(totpCode ? { totpCode } : {}) });
  };

  const back = (): void => {
    // Start over from the password: an expired binding cannot be revived, and a
    // fresh sign-in is what issues a fresh one.
    setEnrollment(null);
    setPassword('');
    mutation.reset();
    enroll.reset();
  };

  return (
    <div className="signin">
      <div className={`signin-inner ${enrollment ? 'signin-inner-wide' : ''}`}>
        <div className="signin-card">
          <header className="signin-head">
            {enrollment ? (
              <>
                <Logo height={36} />
                <div>
                  <h1 className="signin-title">Настройка входа</h1>
                  <p className="page-subtitle">
                    Для вашей роли нужен второй фактор. Привяжите приложение — это делается один раз.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* The logo is the heading: its text alternative is the product's name. */}
                <h1>
                  <Logo height={36} />
                </h1>
                <p className="page-subtitle">{BRAND.tagline}</p>
              </>
            )}
          </header>

          {enrollment ? (
            <div className="stack">
              <AuthenticatorSetup
                material={enrollment}
                busy={enroll.isPending}
                error={enrollError}
                submitLabel="Привязать и войти"
                onSubmit={(code) => enroll.mutate({ token: enrollment.token, code })}
              />
              <Button variant="ghost" icon={ChevronLeft} onClick={back}>
                Назад ко входу
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <Field label="Логин" htmlFor="login">
                <Input
                  id="login"
                  value={login}
                  onChange={(event) => setLogin(event.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
              </Field>

              <Field label="Пароль" htmlFor="password">
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>

              {mfaRequired ? (
                <Field
                  label="Код подтверждения"
                  htmlFor="totp"
                  hint="Шесть цифр из приложения-аутентификатора"
                >
                  <Input
                    id="totp"
                    value={totpCode}
                    onChange={(event) => setTotpCode(event.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    autoFocus
                    required
                  />
                </Field>
              ) : null}

              <Button type="submit" variant="primary" size="lg" block busy={mutation.isPending}>
                {mutation.isPending ? 'Входим…' : 'Войти'}
              </Button>

              {failed ? (
                <p className="field-error row" role="alert">
                  <CircleAlert size={14} aria-hidden="true" />
                  {message}
                </p>
              ) : null}
            </form>
          )}

          <div className="signin-foot">
            <ThemeSwitch />
          </div>
        </div>

        <p className="refreshing">
          <ShieldCheck size={12} aria-hidden="true" />
          Владельцам и администраторам вход только с кодом подтверждения
        </p>
      </div>
    </div>
  );
}
